import React from 'react';
import { Controller, useFormContext, UseControllerProps } from 'react-hook-form';
import { NumberField } from '@schuettflix/planum-react';
import { SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { CurrencyCode } from '@schuettflix/utils';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { parseNumberTo2Decimals } from '@/shared/utils/parseNumberTo2Decimals.ts';
import { cn } from '@/shared/utils/cn.ts';

type PriceFieldValues = { [key: string]: number };

export type PriceInputProps = Pick<UseControllerProps<PriceFieldValues>, 'name' | 'defaultValue'> & {
    placeholder?: string;
    disabled?: boolean;
    unit?: SUPPORTED_UNITS;
    currencyCode?: CurrencyCode;
    testId?: string;
    onPriceChange?: (value?: number) => void;
    className?: string;
};

export const PriceInput = React.forwardRef<HTMLInputElement, PriceInputProps>(
    (
        { name = 'price', defaultValue, disabled, unit, currencyCode, testId, onPriceChange, placeholder, className },
        _ref
    ) => {
        const { control } = useFormContext();
        const { t } = useTranslation();
        const { getCurrencySymbol } = useFormatters();

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                render={({ field: { onChange, value, ...field }, fieldState }) => (
                    <NumberField
                        {...field}
                        value={typeof value === 'number' ? value : undefined}
                        size="lg"
                        className={cn('w-full', className)}
                        trailingSlot={
                            <span>{`${getCurrencySymbol(currencyCode)}${unit ? ' / ' + t(SUPPORTED_UNIT_LABEL[unit]) : ''}`}</span>
                        }
                        onValueChange={val => {
                            const parsed = typeof val === 'number' ? parseNumberTo2Decimals(val) : undefined;
                            const current = parsed ? Number(parsed) : undefined;
                            onPriceChange?.(current);
                            onChange(current);
                        }}
                        minimumFractionDigits={2}
                        step={0.01}
                        disabled={disabled}
                        error={fieldState.error?.message}
                        placeholder={placeholder ?? t('forms.inputs.PriceAndTaxClassInput.price')}
                        aria-label={placeholder ?? t('forms.inputs.PriceAndTaxClassInput.price')}
                        data-test={testId}
                    />
                )}
            />
        );
    }
);
